import type { DateRange } from '@daypicker/react';
import { endOfYear, startOfYear } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { DateRangeCalendar, type DateRangePreset } from '@/components/ui/DateRangePicker';
import { formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';

export const DATE_FILTER_CONTENT_CLASSNAME = 'w-auto max-w-[calc(100vw-2rem)] p-0';

export function formatDateRangeSummary(
  range: DateRange | undefined,
  locale: RegionalDateLocale,
): string {
  if (range?.from && range.to) {
    return `${formatRegionalDate(range.from, locale)} – ${formatRegionalDate(range.to, locale)}`;
  }
  if (range?.from) return `From ${formatRegionalDate(range.from, locale)}`;
  if (range?.to) return `To ${formatRegionalDate(range.to, locale)}`;
  return '';
}

export function DateRangeFilterEditor({
  value,
  onChange,
  years,
}: {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  years: number[];
}) {
  const clearSelection = useCallback(() => onChange(undefined), [onChange]);

  const yearPresets = useMemo<DateRangePreset[]>(
    () => years.map(year => {
      const date = new Date(year, 0, 1);
      return {
        id: String(year),
        label: String(year),
        range: { from: startOfYear(date), to: endOfYear(date) },
      };
    }),
    [years],
  );

  return (
    <DateRangeCalendar
      value={value}
      onChange={onChange}
      initialMonth={value?.from ?? (years.length > 0 ? new Date(years[0], 0, 1) : undefined)}
      customPresets={years.length > 1 ? yearPresets : undefined}
      showClearSelection
      showRangeSummary
      showTopControls
      onClearSelection={clearSelection}
    />
  );
}
